import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { FileStructureContext } from '../../../contexts/FileStructureContext';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import { updateDashboardFunc } from '../../../lib/helper';
import { CompanyFile, Dashboard, EmailBody } from '../../../types/files';
import ArrayUtils from '../../../utils/ArrayUtils';
import EmailsModal from '../../modals/tables/dashboards/rows/OldEmailsModal-X';
import CompanyMiniDeckViewer from '../../molecules/dashboard-deck/CompanyMiniDeckViewer';
import { ActivityTimeline } from '../../molecules/weeklyPipeline/ActivityTimeline';
import DealContextSection from '../../molecules/dashboard-details-v2/DealContextSection';
import TimelineFilterBar from '../../molecules/dashboard-details-v2/TimelineFilterBar';
import TimelineEntry, { EmailWithDocs } from '../../molecules/dashboard-details-v2/TimelineEntry';

const edjsParser = require("editorjs-parser").default;
const parser = new edjsParser();

type TimelineCardProps = {
    dashboard: Dashboard | null;
};

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: theme.spacing(1),
    },
    bodyWrapper: {
        display: 'flex',
    },
    bodyLeft: {
        flex: 1,
        minWidth: 0,
    },
    bodyRight: {
        width: 340,
        flexShrink: 0,
        padding: theme.spacing(3),
        paddingLeft: 0,
    },
    content: {
        padding: theme.spacing(3),
    },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

type DealContext =
    | { isPlainText: true; text: string }
    | { isPlainText: false; sections: { label: string; text: string }[]; commentary: string; context: string; decision: string };

function parseDealContext(commentaryNote: string): DealContext | null {
    if (!commentaryNote) return null;

    const labelRegex = /(^|\n)(Commentary|Context|Decision):\s*/gi;
    const matches: RegExpExecArray[] = [];
    let match: RegExpExecArray | null;

    while ((match = labelRegex.exec(commentaryNote)) !== null) {
        matches.push(match);
    }

    if (matches.length === 0) {
        return { isPlainText: true, text: commentaryNote };
    }

    const sections: { label: string; text: string }[] = [];
    for (let i = 0; i < matches.length; i++) {
        const m = matches[i];
        const label = m[2];
        const startOfText = m.index + m[0].length;
        const endOfText = i < matches.length - 1 ? matches[i + 1].index : commentaryNote.length;
        sections.push({ label, text: commentaryNote.slice(startOfText, endOfText).trim() });
    }

    return {
        isPlainText: false,
        sections,
        commentary: sections.find(s => s.label.toLowerCase() === 'commentary')?.text || '',
        context:    sections.find(s => s.label.toLowerCase() === 'context')?.text || '',
        decision:   sections.find(s => s.label.toLowerCase() === 'decision')?.text || '',
    };
}

// ─── Component ────────────────────────────────────────────────────────────────

const TimelineCard: React.FC<TimelineCardProps> = ({ dashboard }) => {
    const classes = useStyles();
    const { fileStructure } = useContext(FileStructureContext);
    const { dashboardOwner } = useBackwardsCompatible();

    const [comModalOpen, setComModalOpen] = useState(false);
    const [deckViewerModalOpen, setDeckViewerModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<CompanyFile | undefined>(undefined);
    const [emailActivity, setEmailActivity] = useState<EmailBody>({} as EmailBody);
    const [filter, setFilter] = useState('All');

    const files = useMemo(() =>
        fileStructure
            .filter(file => !file.isDirectory && file.dashboardId === dashboard?.id)
            .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt))),
    [dashboard, fileStructure]);

    const [createEmail, ...otherEmails] = useMemo<EmailWithDocs[]>(() => {
        const activityItems = dashboard?.emailBodies
            ?.filter(eb => ['EMAIL', 'FULL_EMAIL'].includes(eb?.type || '')) as EmailBody[];
        const emailActivities = ArrayUtils.sortByAscending(activityItems ?? [], 'createdAt');

        const fileMatchedMap = new Map<string, boolean>();
        const linkMatchedMap = new Map<string, boolean>();
        const unmatchedFiles: CompanyFile[] = [];
        const unmatchedLinks: string[] = [];

        const emailsWithDocs: EmailWithDocs[] = emailActivities.map(emailBody => ({
            ...emailBody,
            attached: (() => {
                const matched = new Map<string, CompanyFile>();
                const attachments = emailBody.attachments?.map((n: string) => n.trim().toLowerCase()) || [];
                files.forEach(file => {
                    const timeDiff = moment(file.createdAt).diff(moment(emailBody.createdAt), 'minutes', true);
                    if (attachments.includes(file.name.trim().toLowerCase()) || (timeDiff >= 0 && timeDiff <= 5)) {
                        if (!matched.has(file.id)) {
                            matched.set(file.id, file);
                            fileMatchedMap.set(file.id, true);
                        }
                    }
                });
                return Array.from(matched.values());
            })(),
            linked: (() => {
                const seen = new Set<string>();
                const matchedLinks: string[] = [];
                const linksFromEmail = emailBody.externalLinks?.map((l: string) => l.trim()) || [];
                (dashboard?.externalLinks || []).forEach(link => {
                    const t = link.trim();
                    if ((linksFromEmail.includes(t) || (emailBody?.content || '').includes(t)) && !seen.has(t)) {
                        seen.add(t);
                        matchedLinks.push(t);
                        linkMatchedMap.set(t, true);
                    }
                });
                return matchedLinks;
            })(),
        }));

        files.forEach(file => { if (!fileMatchedMap.has(file.id)) unmatchedFiles.push(file); });
        dashboard?.externalLinks?.forEach(link => { if (!linkMatchedMap.has(link.trim())) unmatchedLinks.push(link); });

        if (emailsWithDocs.length > 0) {
            if (unmatchedFiles.length > 0) emailsWithDocs[0].attached = [...emailsWithDocs[0].attached, ...unmatchedFiles];
            if (unmatchedLinks.length > 0) emailsWithDocs[0].linked   = [...emailsWithDocs[0].linked, ...unmatchedLinks];
        } else if (unmatchedFiles.length > 0 || unmatchedLinks.length > 0) {
            emailsWithDocs.push({
                id: `${dashboard?.id}-unmatched`,
                createdAt: unmatchedFiles[0]?.createdAt || dashboard?.createdAt,
                type: 'EMAIL',
                attached: unmatchedFiles,
                linked: unmatchedLinks,
            } as EmailWithDocs);
        }

        return emailsWithDocs;
    // eslint-disable-next-line
    }, [dashboard, files]);

    const commentaryNote = useMemo(() => {
        const note = dashboard?.notes?.find(n => n.title === `$['Commentary']`);
        return new DOMParser()
            .parseFromString(parser.parse({ blocks: (JSON.parse(note?.data || '[]') as any[]).slice(1) }), 'text/html')
            ?.body?.textContent?.trim() || '';
    }, [dashboard?.notes]);

    const dealContext = useMemo(() => parseDealContext(commentaryNote), [commentaryNote]);

    const filterOptions = useMemo(() => {
        const extra = (!!files?.length || !!dashboard?.externalLinks?.length) ? ['Documents'] : [];
        return ['All', ...extra];
    // eslint-disable-next-line
    }, [dashboard, files]);

    const handleFileClick = (file: CompanyFile) => {
        setSelectedFile(file);
        setDeckViewerModalOpen(true);
    };

    const handleViewEmail = (email: EmailBody) => {
        setEmailActivity(email);
        setComModalOpen(true);
    };

    const handleDeleteLink = useCallback((link: string) => {
        if (!dashboard) return;
        const updatedLinks = (dashboard.externalLinks || []).filter((l: string) => l.trim() !== link.trim());
        updateDashboardFunc({ ...dashboard, externalLinks: updatedLinks });
    // eslint-disable-next-line
    }, [dashboard]);

    const hasContent = otherEmails.length > 0 || createEmail || !!files?.length;

    return (
        <>
            <Paper elevation={0} className={classes.paper}>
                <Box className={classes.bodyWrapper}>
                    <Box className={classes.bodyLeft}>
                        {dealContext && (
                            <Box className={classes.content}>
                                <DealContextSection
                                    isPlainText={dealContext.isPlainText}
                                    text={dealContext.isPlainText ? dealContext.text : undefined}
                                    context={!dealContext.isPlainText ? dealContext.context : undefined}
                                    commentary={!dealContext.isPlainText ? dealContext.commentary : undefined}
                                    decision={!dealContext.isPlainText ? dealContext.decision : undefined}
                                />
                            </Box>
                        )}

                        <TimelineFilterBar
                            title="Email Timeline"
                            options={filterOptions}
                            activeFilter={filter}
                            onFilterChange={setFilter}
                        />

                        <Box className={classes.content}>
                            <Stack spacing={3} divider={<Divider />}>
                                {otherEmails.reverse().map((email, i) => (
                                    <TimelineEntry
                                        key={i}
                                        email={email}
                                        metaText={`From: ${email.from?.replace(/<.*?>/g, '')}`}
                                        allFiles={files}
                                        filter={filter}
                                        onFileClick={handleFileClick}
                                        onViewEmailClick={() => handleViewEmail(email)}
                                        onDeleteLink={handleDeleteLink}
                                    />
                                ))}

                                {createEmail ? (
                                    <TimelineEntry
                                        email={{ ...createEmail, createdAt: createEmail.createdAt || dashboard?.createdAt }}
                                        metaText={`From: ${createEmail.from?.replace(/<.*?>/g, '')}`}
                                        allFiles={files}
                                        filter={filter}
                                        onFileClick={handleFileClick}
                                        onViewEmailClick={() => handleViewEmail(createEmail)}
                                        onDeleteLink={handleDeleteLink}
                                    />
                                ) : !!files?.length && (
                                    <TimelineEntry
                                        email={{
                                            id: 'file-upload',
                                            createdAt: dashboard?.createdAt,
                                            type: 'EMAIL',
                                            attached: [],
                                            linked: [],
                                        } as EmailWithDocs}
                                        subject="File uploaded"
                                        metaText={`Added by: ${dashboardOwner?.name}`}
                                        allFiles={files}
                                        filter={filter}
                                        showViewEmail={false}
                                        onFileClick={handleFileClick}
                                        onViewEmailClick={() => {}}
                                    />
                                )}

                                {!hasContent && (
                                    <Stack alignItems="center" justifyContent="center" height="50vh" pb={4}>
                                        <Typography fontFamily="Lato" fontWeight="600" color="#2c3e50">
                                            No email or files yet.
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    </Box>

                    <Box className={classes.bodyRight}>
                        <ActivityTimeline dashboard={dashboard!} />
                    </Box>
                </Box>
            </Paper>

            {deckViewerModalOpen && (
                <CompanyMiniDeckViewer
                    open={deckViewerModalOpen}
                    defaultDeck={selectedFile}
                    externalDeck={dashboard?.externalLinks?.at(-1)}
                    onClose={() => setDeckViewerModalOpen(false)}
                />
            )}
            {comModalOpen && (
                <EmailsModal
                    open={comModalOpen}
                    emailActivities={[emailActivity]}
                    dashboard={dashboard!}
                    onClose={() => setComModalOpen(false)}
                />
            )}
        </>
    );
};

export default TimelineCard;
